/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { DOCUMENT_TITLE_SUFFIX, documentTitleFor, useResolvedPageTitle } from '@/lib/pageTitle';

/** Target of the skip link; also the focus target once someone follows it. */
export const MAIN_CONTENT_ID = 'main-content';

export default function Layout() {
  const pageTitle = useResolvedPageTitle();

  // Accessibility survey #12: `document.title` was set once in index.html, so
  // every tab read "AMIDA LMS" and nothing changed on navigation. One owner
  // for it, here, because Layout is the single mount point for every route
  // that has the app chrome.
  useEffect(() => {
    document.title = documentTitleFor(pageTitle);
    // Routes outside Layout (login, the auth callback) set no title of their
    // own, so without this the tab still read "Dashboard · AMIDA LMS" on the
    // sign-in screen after a logout.
    return () => {
      document.title = DOCUMENT_TITLE_SUFFIX;
    };
  }, [pageTitle]);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/*
        Skip link: visually hidden until focused, first control in the tab
        order. Without it every keyboard user tabbed the whole sidebar on
        every page before reaching the content.
      */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
      >
        Skip to main content
      </a>
      {/*
        Route announcer. Focus is deliberately NOT moved on navigation — tab
        switches inside a page are route changes here, and yanking focus to
        <main> on each one would fight the user. The new page name is
        announced instead.

        No `key` here, deliberately: re-keying would unmount and remount this
        <p>, and screen readers announce a mutation to a region already in the
        accessibility tree, not a node that arrives with its text already in
        place. The element has to be stable and its text has to change.
      */}
      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {pageTitle}
      </p>
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header />
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className="flex-1 overflow-auto focus:outline-none"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
